import { falSlidersSimple } from 'shared/@generated/icons/falSlidersSimple';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import { Props, Breeder, Animal, Breed } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { apiRoute } from 'shared/library/api-route';

import { BreederLocation, BreedersMap } from 'shared/components/breeders-map';
import { BreedersMapStatus } from 'shared/components/breeders-map-status';
import { BreederMapCard } from 'shared/components/breeder-map-card';
import useApi from 'shared/library/use-api';
import { notify } from 'shared/library/notify';
import { Text } from 'shared/components/text';
import { Button } from 'shared/components/button';
import { BreedInput, BreedInputValues } from 'shared/components/form/breed-input';
import { BreedMultiSelect } from 'shared/components/form/breed-multi-select';
import { TextInput } from 'shared/components/form/text-input';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { PageBuilder } from 'shared/library/page-builder';
import { useDebouncedValue } from 'shared/library/use-debounced-value';
import { useIsLoggedIn } from 'shared/state/user';
import { ExpandableSelect } from 'shared/components/form/expandable-select';
import { ExpandableBase } from 'shared/components/form/expandable-base';
import { ControlledBreedersWebFilterControls } from 'shared/components/breeders-web-filter-controls';
import { BreedersMapFilter, parseBreedersMapFilter } from 'shared/state/breeders-filter';
import { useDebouncedQueryFilter } from 'shared/library/use-debounced-query-filter';
import { buildQuery, normalizeQuery } from 'shared/library/use-debounced-query-filter';
import { useRouter } from 'shared/library/use-router';
import { useDevice } from 'shared/library/use-device';

type IndexBreedersMapFilters = BreedersMapFilter;

const defaultIndexBreedersMapFilters: IndexBreedersMapFilters = {
  breeds: [],
  query: '',
  animal: null,
};

interface IndexBreedersMapScreenProps extends Props {
  initialFilters: IndexBreedersMapFilters;
  lockedBreed?: Pick<Breed, 'id' | 'animal'>;
}

type BreedersMapResponse = {
  breeders: BreederLocation[];
  total: number;
};

const indexBreedersMapTranslationPaths = [
  'common',
  'components',
  'library',
  'screens.breeders.index_breeders_map',
  'screens.breeders.show_breeder',
  'web',
];

function getBreedIds(value: BreedInputValues): number[] {
  return value.filter((breed): breed is number => typeof breed === 'number');
}

function BreederNameFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation('components');
  const trimmedValue = value.trim();

  return (
    <ExpandableBase
      title={t('breeders_filter_form.kennel_name')}
      currentLabel={trimmedValue || t('breeders_filter_form.all_breeders')}
    >
      <View className={'p-3 pt-1'}>
        <TextInput
          dense={true}
          placeholder={t('breeders_filter_form.search_by_name')}
          value={value}
          onChangeText={onChange}
        />
      </View>
    </ExpandableBase>
  );
}

const IndexBreedersMapScreen = ({ initialFilters, lockedBreed }: IndexBreedersMapScreenProps) => {
  const { t } = useTranslation('screens');
  const { asPath, replace } = useRouter();
  const [breeders, setBreeders] = useState<BreederLocation[]>([]);
  const [totalBreeders, setTotalBreeders] = useState(0);
  const [hasLoadedBreeders, setHasLoadedBreeders] = useState(false);
  const [selectedBreederSlug, setSelectedBreederSlug] = useState<string | null>(null);
  const [selectedBreeder, setSelectedBreeder] = useState<Breeder | null>(null);
  const selectedBreederRequest = useRef<AbortController | null>(null);
  const { width, md } = useDevice();
  const isDesktop = Platform.OS === 'web' && width >= md;
  const lockedBreedId = lockedBreed?.id ?? null;
  const lockedAnimal = lockedBreed?.animal ?? null;
  const [breeds, setBreeds] = useState<number[]>(() =>
    lockedBreedId ? [lockedBreedId] : getBreedIds(initialFilters.breeds),
  );
  const [query, setQuery] = useState(initialFilters.query);
  const [animal, setAnimal] = useState<Animal | null>(lockedAnimal ?? initialFilters.animal);
  const [nativeFilterOpen, setNativeFilterOpen] = useState(false);
  const isLoggedIn = useIsLoggedIn();
  const { request: requestMap } = useApi();
  const { request } = useApi();
  const debouncedQuery = useDebouncedValue(query.trim());
  const appliedFilterCount =
    (breeds.length > 0 ? 1 : 0) + (animal ? 1 : 0) + (query.trim() ? 1 : 0);
  const selectedBreederActiveListingsCount =
    breeders.find((breeder) => breeder.slug === selectedBreederSlug)?.active_listings_count ?? 0;

  const initialRouteFilters = useMemo(
    () => ({
      ...initialFilters,
      ...(lockedBreedId ? { breeds: [] } : {}),
      ...(lockedAnimal ? { animal: null } : {}),
    }),
    [initialFilters, lockedAnimal, lockedBreedId],
  );
  const routeFilters = useMemo(
    () => ({
      breeds: lockedBreedId ? [] : breeds,
      query,
      animal: lockedAnimal ? null : animal,
    }),
    [animal, breeds, lockedAnimal, lockedBreedId, query],
  );
  // Keep native modal edits local until its close/apply action.
  const routeFilterRef = useRef(routeFilters);
  if (!nativeFilterOpen) routeFilterRef.current = routeFilters;

  function setSelectedBreeds(values: BreedInputValues) {
    setBreeds(lockedBreedId ? [lockedBreedId] : getBreedIds(values));
  }

  function resetFilters() {
    setBreeds(lockedBreedId ? [lockedBreedId] : []);
    setAnimal(lockedAnimal);
    setQuery('');
  }

  useDebouncedQueryFilter(initialRouteFilters, routeFilterRef.current, (isLocalNavigation) => {
    if (isLocalNavigation || nativeFilterOpen) {
      return;
    }

    setBreeds(lockedBreedId ? [lockedBreedId] : getBreedIds(initialFilters.breeds));
    setAnimal(lockedAnimal ?? initialFilters.animal);
    setQuery(initialFilters.query);
  });

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    const [pathWithQuery = ''] = asPath.split('#');
    const [pathname = '', currentQuery = ''] = pathWithQuery.split('?');
    const canonicalQuery = buildQuery(initialRouteFilters);

    if (normalizeQuery(currentQuery) !== canonicalQuery) {
      void replace(canonicalQuery ? `${pathname}?${canonicalQuery}` : pathname, undefined);
    }
  }, [asPath, initialRouteFilters, replace]);

  useEffect(() => {
    const abortController = new AbortController();
    const filters = {
      map: true,
      ...(breeds.length > 0 ? { breed_ids: breeds } : {}),
      ...(debouncedQuery ? { query: debouncedQuery } : {}),
      ...(animal ? { animal } : {}),
    };

    clearSelectedBreeder();
    setHasLoadedBreeders(false);

    requestMap<BreedersMapResponse>(apiRoute('breeders.index'), filters, undefined, abortController)
      .then((data) => {
        if (abortController.signal.aborted) {
          return;
        }

        setBreeders(data.breeders);
        setTotalBreeders(data.total);
        setHasLoadedBreeders(true);
      })
      .catch((err) => {
        if (err?.report === false) {
          return;
        }

        notify.error(err, 'index-breeders-map.tsx - loadMapBreeders');
      });

    return () => abortController.abort();
  }, [breeds, debouncedQuery, animal]);

  useEffect(() => () => selectedBreederRequest.current?.abort(), []);

  function clearSelectedBreeder() {
    selectedBreederRequest.current?.abort();
    setSelectedBreederSlug(null);
    setSelectedBreeder(null);
  }

  function loadSelectedBreeder(slug: string) {
    selectedBreederRequest.current?.abort();
    const controller = new AbortController();
    selectedBreederRequest.current = controller;
    setSelectedBreederSlug(slug);
    setSelectedBreeder(null);

    request<{ breeder: Breeder }>(
      apiRoute('breeders.show', { breeder: slug }),
      { compact: true },
      undefined,
      controller,
    )
      .then((data) => {
        if (!controller.signal.aborted) setSelectedBreeder(data.breeder);
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.report === false) return;
        clearSelectedBreeder();
        notify.error(err, 'index-breeders-map.tsx - loadBreeder');
      });
  }

  const selectedBreederCard = selectedBreederSlug ? (
    <BreederMapCard
      breeder={selectedBreeder}
      activeListingsCount={selectedBreederActiveListingsCount}
      onClose={clearSelectedBreeder}
    />
  ) : null;

  return (
    <View className={isLoggedIn ? 'h-body' : 'h-body-without-tabs'}>
      <BreedersMap
        breeders={breeders}
        selectedBreederSlug={selectedBreederSlug}
        onSelect={(properties) => loadSelectedBreeder(properties.slug)}
        onDeselect={clearSelectedBreeder}
      >
        {isDesktop && selectedBreederCard}
      </BreedersMap>

      <View pointerEvents={'box-none'} className={'z-100 absolute left-0 top-0 w-full p-3'}>
        <View pointerEvents={'auto'} className={'items-start self-start'}>
          {Platform.OS === 'web' ? (
            <ControlledBreedersWebFilterControls
              breeds={breeds}
              animal={animal}
              query={query}
              totalBreeders={totalBreeders}
              showAnimal={true}
              showQuery={true}
              multipleBreeds={true}
              allowBreedQuery={false}
              floating={true}
              lockBreed={Boolean(lockedBreedId)}
              lockAnimal={Boolean(lockedAnimal)}
              onBreedsChange={setSelectedBreeds}
              onAnimalChange={(value) => setAnimal(lockedAnimal ?? value)}
              onQueryChange={setQuery}
              onClear={resetFilters}
            />
          ) : (
            <FullscreenModal
              headerTitle={t('components:breeders_filter_form.filter_breeders')}
              onClose={() => setNativeFilterOpen(false)}
              headerRight={() => (
                <TouchableOpacity className={'p-2'} onPress={resetFilters}>
                  <Text className={'text-sm font-bold text-red-700'}>{t('common:clear')}</Text>
                </TouchableOpacity>
              )}
              trigger={({ open }) => (
                <Button
                  icon={falSlidersSimple}
                  iconPosition={'left'}
                  text={t('components:breeders_filter_form.filter')}
                  color={'white'}
                  dense={true}
                  onPress={() => {
                    setNativeFilterOpen(true);
                    open();
                  }}
                >
                  {appliedFilterCount > 0 && (
                    <Text
                      className={
                        'bg-brand-cream-dark text-brand-brown-dark ml-1.5 w-5 rounded py-0.5 text-center text-xs font-bold'
                      }
                    >
                      {appliedFilterCount}
                    </Text>
                  )}
                </Button>
              )}
              footer={({ close }) => (
                <Button
                  text={t('components:breeders_filter_form.view_breeders')}
                  onPress={close}
                  center={true}
                >
                  <Text className={'ml-3 rounded bg-black/10 p-1 text-sm font-black text-white'}>
                    {totalBreeders}
                  </Text>
                </Button>
              )}
            >
              <View className={'gap-1.5'} testID={'breeders-filter-form'}>
                {lockedBreedId ? (
                  <BreedInput
                    value={breeds}
                    onChange={setSelectedBreeds}
                    allowQuery={false}
                    historyEnabled={false}
                    animal={animal ?? undefined}
                    placeholder={t('components:breeders_filter_form.breed_placeholder')}
                  />
                ) : (
                  <BreedMultiSelect
                    value={breeds}
                    onChange={setSelectedBreeds}
                    allowQuery={false}
                    historyEnabled={false}
                    animal={animal ?? undefined}
                    placeholder={t('components:breeders_filter_form.breed_placeholder')}
                  />
                )}

                <ExpandableSelect<Animal | null>
                  title={t('components:breeders_filter_form.animal')}
                  options={[
                    {
                      label: t('components:breeders_filter_form.all_animals'),
                      value: null,
                    },
                    ...Object.entries(Animal).map(([_key, value]) => ({
                      label: t(`common:enums.animal.${value}`),
                      value,
                    })),
                  ]}
                  value={animal}
                  onChange={(value) => setAnimal(lockedAnimal ?? value)}
                />

                <BreederNameFilter value={query} onChange={setQuery} />
              </View>
            </FullscreenModal>
          )}
        </View>

        <View className={'mt-2 self-start'}>
          <BreedersMapStatus
            viewing={breeders.length}
            total={totalBreeders}
            loading={!hasLoadedBreeders}
          />
        </View>
      </View>

      {!isDesktop && selectedBreederCard && (
        <View
          className={'z-max absolute bottom-0 left-1/2 w-full max-w-[21.5rem] -translate-x-1/2 p-4'}
        >
          {selectedBreederCard}
        </View>
      )}
    </View>
  );
};

IndexBreedersMapScreen.pageBuilder = new PageBuilder<
  IndexBreedersMapScreenProps,
  IndexBreedersMapFilters
>()
  .translationPaths(indexBreedersMapTranslationPaths)
  .request(async ({ params }) => ({
    initialFilters: parseBreedersMapFilter(params),
  }));

export { IndexBreedersMapScreen, defaultIndexBreedersMapFilters, indexBreedersMapTranslationPaths };
export type { IndexBreedersMapFilters, IndexBreedersMapScreenProps };
